import React, { useState } from "react";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  Tooltip,
} from "@mui/material";
import dayjs from "dayjs";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CloseIcon from "../../../../assets/icons/closeIcon.svg";
import "./company-dashboard-edit-object-popup.scss";
import { SelectInputComponent } from "../../../../common/components";
import DatePickerComponent from "../../../../common/components/date-picker/date-picker-compoenent";
import { debounce } from "lodash";
import { getObjectiveTitle } from "../../../../api/title-search";
export interface StatusOption {
  statusId: number;
  statusName: string;
}
/**
 * CompanyDashboardEditObjectivePopup
 *
 * Dialog used to edit an existing Objective in the Company Dashboard context.
 * Handles owners multi-select, status, and year date globalThis. on submit, emits
 * a normalized payload via onSave and closes.
 *
 * Props (any-typed for compatibility):
 * - isOpenAddPopup: boolean — controls dialog visibility
 * - onCancel: () => void — close handler
 * - newObjectiveData, setNewObjectiveData: object + setter for working copy
 * - employeesList: Array<any> — used to populate Owners dropdown
 * - statusListObjectives: Array<StatusOption> — status dropdown options
 * - onSave: (payload: any) => void — submit handler
 */
const CompanyDashboardEditObjectivePopup: any = (props: any) => {
  const {
    isOpenAddPopup,
    onCancel,
    newObjectiveData,
    setNewObjectiveData,
    employeesList,
    onSave,
  } = props;
  const [titleSuggestions, setTitleSuggestions] = useState<any[]>([]);
  const [loadingTitles, setLoadingTitles] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);

  // Clear search state when popup closes
  React.useEffect(() => {
    if (!isOpenAddPopup) {
      setTitleSuggestions([]);
      setHasSearched(false);
      setAutocompleteOpen(false);
      setLoadingTitles(false);
    }
  }, [isOpenAddPopup]);

  // Debounced function to search for initiative titles
  const searchObjectiveTitles = React.useMemo(
    () =>
      debounce(async (searchValue: string) => {
        if (!searchValue || searchValue.trim().length < 2) {
          setTitleSuggestions([]);
          setHasSearched(false);
          return;
        }

        setLoadingTitles(true);
        setHasSearched(true);
        try {
          const response = await getObjectiveTitle({ title: searchValue });
          const objectives = response?.data?.data || [];
          setTitleSuggestions(objectives);
        } catch (error: any) {
          setTitleSuggestions([]);
          console.error(error);
        } finally {
          setLoadingTitles(false);
        }
      }, 500),
    [],
  );
  const validationSchema = Yup.object().shape({
    title: Yup.string().required("Title is required"),
    owner: Yup.array().min(1, "Owner is required"),
  });

  const soltsPaperTitle = (props: any, values: any) => {
    const showNoResults =
      !loadingTitles &&
      hasSearched &&
      titleSuggestions.length === 0 &&
      values.title.trim().length >= 2;

    const showLoading = loadingTitles;

    const renderPaperContent = () => {
      if (showLoading) {
        return (
          <div
            style={{
              padding: "8px 16px",
              color: "#666",
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            Searching...
          </div>
        );
      }
      if (showNoResults) {
        return (
          <div
            style={{
              padding: "8px 16px",
              color: "#666",
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            No results found.
          </div>
        );
      }
      if (titleSuggestions.length === 0) {
        return (
          <div
            style={{
              padding: "8px 16px",
              color: "#666",
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            Search
          </div>
        );
      }
      return props.children;
    };

    return (
      <div
        {...props}
        style={{
          backgroundColor: "#fff",
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 8px",
            background: "#fff",
            borderBottom: "0.5px solid #eee",
          }}
        >
          <div className="" style={{ fontSize: "14px" }}>
            <InputLabel style={{ fontSize: "14px" }}>
              Existing Objectives
            </InputLabel>
          </div>
          <button
            style={{
              background: "transparent",
              border: "none",
              padding: "0px",
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setTitleSuggestions([]);
              setHasSearched(false);
              setAutocompleteOpen(false);
            }}
          >
            <img
              alt="Close"
              src={CloseIcon}
              style={{
                cursor: "pointer",
                color: "#555",
                margin: "4px 0",
              }}
            />
          </button>
        </div>
        {renderPaperContent()}
      </div>
    );
  };
  return (
    <Dialog
      open={isOpenAddPopup}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className={`company-dashboard-add-popup-container `}
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        onCancel();
      }}
    >
      <DialogTitle id="alert-dialog-title">
        <div className="header-wrapper">
          <div className="header-wrapper-text">Edit Objective</div>
          <div className="image-wrapper">
            <button
              style={{
                background: "transparent",
                border: "none",
                padding: 0,
                cursor: "pointer",
              }}
              onClick={onCancel}
            >
              <img
                src={CloseIcon}
                className="close-icon"
                style={{ cursor: "pointer" }}
                alt="close"
              />
            </button>
          </div>
        </div>
      </DialogTitle>
      <DialogContent>
        <Formik
          initialValues={{
            selectedOption: "Objective",
            owner:
              newObjectiveData?.owners?.length > 0
                ? newObjectiveData?.owners?.map(
                    (owner: any) =>
                      employeesList?.find(
                        (emp: any) => emp.employeeId === owner.employeeId,
                      )?.employeeFullName || "",
                  )
                : [],
            ownerId:
              newObjectiveData?.owners?.map((o: any) => o.employeeId) || [],
            title: newObjectiveData?.objectiveName || "",
            //   ) || null,
            createdAt: newObjectiveData?.createdAt
              ? dayjs(newObjectiveData.createdAt)
              : dayjs(), // Current or existing created date
            year:
              newObjectiveData?.year ||
              (newObjectiveData?.startDate
                ? dayjs(newObjectiveData.startDate).year()
                : dayjs().year()), // Extract year from existing data or use current
          }}
          validationSchema={validationSchema}
          validateOnChange={true}
          enableReinitialize={true}
          onSubmit={(values, { resetForm }) => {
            const originalOwnerIds =
              newObjectiveData?.owners?.map((o: any) => o.employeeId) || [];

            const currentOwnerIds = values.ownerId || [];

            const ownerIds = [
              ...originalOwnerIds
                .filter((id: number) => !currentOwnerIds.includes(id))
                .map((id: number) => ({ ownerId: id, isRemove: 1 })),
              ...currentOwnerIds.map((id: number) => ({
                ownerId: id,
                isRemove: 0,
              })),
            ];

            // Calculate startDate and endDate based on selected year
            const selectedYear = values.year;
            const startDate = dayjs().year(selectedYear).month(0).date(1); // January 1st
            const endDate = dayjs().year(selectedYear).month(11).date(31); // December 31st
            const createdAt = values.createdAt; // Current or existing created date

            const newObjective = {
              id: newObjectiveData.pk,
              objectiveName: values.title,
              year: selectedYear,
              createdAt: createdAt,
              startDate: startDate,
              endDate: endDate,
              ownerIds,
            };

            setNewObjectiveData(newObjective);
            onSave(newObjective);
            onCancel();
            resetForm();
          }}
        >
          {({
            values,
            setFieldValue,
            touched,
            validateField,
            isValid,
            submitCount,
          }) => (
            <Form>
              <div className="create-lead-capture-popup-confirmation-description-checkbox-input">
                <div className="form-group form-text-input-component">
                  <InputLabel>Title</InputLabel>
                  <Autocomplete
                    freeSolo
                    options={titleSuggestions}
                    getOptionLabel={(option: any) =>
                      typeof option === "string"
                        ? option
                        : option.objectiveName || ""
                    }
                    loading={loadingTitles}
                    filterOptions={(x) => x}
                    open={autocompleteOpen}
                    onOpen={() => {
                      // Only open if there's meaningful content to show
                      if (
                        values.title.trim().length >= 2 ||
                        titleSuggestions.length > 0 ||
                        loadingTitles
                      ) {
                        setAutocompleteOpen(true);
                      }
                    }}
                    onClose={(event, reason) => {
                      // Always close on blur (clicking outside) or escape key
                      if (reason === "blur" || reason === "escape") {
                        setAutocompleteOpen(false);
                        return;
                      }

                      // For other reasons, keep open if there are results, loading, or no results after search
                      const shouldStayOpen =
                        titleSuggestions.length > 0 ||
                        loadingTitles ||
                        (hasSearched &&
                          !loadingTitles &&
                          values.title.trim().length >= 2);

                      // Only close if shouldn't stay open (not when showing no results)
                      if (!shouldStayOpen) {
                        setAutocompleteOpen(false);
                      }
                    }}
                    disableClearable
                    value={values.title}
                    inputValue={values.title}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === "input") {
                        setFieldValue("title", newInputValue);
                        searchObjectiveTitles(newInputValue);
                        // Open dropdown when typing with enough characters
                        if (newInputValue.trim().length >= 2) {
                          setAutocompleteOpen(true);
                        } else {
                          // Close dropdown when text is cleared or too short
                          setAutocompleteOpen(false);
                          setTitleSuggestions([]);
                          setHasSearched(false);
                        }
                      }
                    }}
                    onChange={(event) => {
                      // Prevent selection - only allow typing
                      event.preventDefault();
                    }}
                    onBlur={() => {
                      // Only clear if we're not showing no results message
                      setTimeout(() => {
                        const shouldKeepOpen =
                          titleSuggestions.length > 0 ||
                          loadingTitles ||
                          (hasSearched &&
                            !loadingTitles &&
                            values.title.trim().length >= 2);

                        if (!shouldKeepOpen) {
                          setTitleSuggestions([]);
                          setHasSearched(false);
                          setAutocompleteOpen(false);
                        }
                      }, 200);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Enter Title"
                        slotProps={{
                          htmlInput: {
                            ...params.inputProps,
                            maxLength: 150,
                          },
                        }}
                      />
                    )}
                    renderOption={(props, option: any) => (
                      <Tooltip title="Selection is turned off for this field.">
                        <li
                          {...props}
                          style={{
                            cursor: "not-allowed",
                            padding: "8px 16px",
                            opacity: 0.7,
                            backgroundColor:
                              values?.title?.trim()?.toLowerCase() ===
                              option.objectiveName?.trim()?.toLowerCase()
                                ? "#ddddfa"
                                : "transparent !important",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor =
                              values?.title?.trim()?.toLowerCase() ===
                              option.objectiveName?.trim()?.toLowerCase()
                                ? "#ddddfa"
                                : "transparent !important";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              values?.title?.trim()?.toLowerCase() ===
                              option.objectiveName?.trim()?.toLowerCase()
                                ? "#ddddfa"
                                : "transparent !important";
                          }}
                        >
                          <div
                            style={{ fontSize: "14px", pointerEvents: "none" }}
                          >
                            {option.objectiveName}
                          </div>
                        </li>
                      </Tooltip>
                    )}
                    noOptionsText={(() => {
                      if (loadingTitles) {
                        return "Searching...";
                      }
                      if (values.title.length < 2) {
                        return "Type at least 2 characters";
                      }
                      return "No results found.";
                    })()}
                    slotProps={{
                      listbox: {
                        style: {
                          maxHeight: "200px",
                        },
                      },
                    }}
                    slots={{ paper: (props) => soltsPaperTitle(props, values) }}
                  />
                  <ErrorMessage
                    name="title"
                    component="div"
                    className="error-message"
                  />
                </div>
                <div className="form-group">
                  <SelectInputComponent
                    fieldName="owner"
                    placeholder="Select Owner"
                    label="Owner"
                    optional={false}
                    disabled={false}
                    multiple={true}
                    isOwnerDropdown={true}
                    options={employeesList
                      ?.filter((emp: any) =>
                        emp?.roles?.find(
                          (role: any) => role?.roleName === "ELT",
                        ) || values.owner?.includes(emp.employeeFullName),
                      )
                      ?.map((emp: any) => {
                        console.log(values.owner, "owner values");
                        
                        return {
                          value: emp.employeeFullName,
                          label: emp.employeeFullName,
                          image: emp.employeeImage || null,
                          avatar: emp.employeeImage
                            ? null
                            : emp.employeeFullName,
                        };
                      })}
                    value={values.owner}
                    onChange={(selected: string[]) => {
                      setFieldValue("owner", selected);
                      const selectedIds = employeesList
                        ?.filter((emp: any) =>
                          selected.includes(emp.employeeFullName),
                        )
                        .map((emp: any) => emp.employeeId);
                      setFieldValue("ownerId", selectedIds);
                      // Trigger validation if form has been submitted before
                      if (touched.owner) {
                        setTimeout(() => {
                          validateField("owner");
                        }, 0);
                      }
                    }}
                  />
                  <ErrorMessage
                    name={"owner"}
                    component="div"
                    className="error-message"
                  />
                </div>
                <div
                  className="form-group"
                  style={{ display: "flex", gap: "16px", alignItems: "end" }}
                >
                  <div style={{ flex: 1 }}>
                    <DatePickerComponent
                      fieldName={"createdAt"}
                      label="Created Date"
                      value={values?.createdAt}
                      disabled={true}
                      onChange={(e: any) => {
                        setFieldValue("createdAt", e);
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <SelectInputComponent
                      fieldName="year"
                      placeholder="Select Year"
                      label="Year"
                      optional={false}
                      disabled={false}
                      multiple={false}
                      options={[
                        {
                          value: dayjs().year(),
                          label: dayjs().year().toString(),
                        },
                        {
                          value: dayjs().year() + 1,
                          label: (dayjs().year() + 1).toString(),
                        },
                      ]}
                      value={values.year}
                      onChange={(e: any) => {
                        setFieldValue("year", e);
                      }}
                    />
                  </div>
                </div>
              </div>
              <DialogActions>
                <div className="action-btn-wrapper">
                  <button
                    type="button"
                    onClick={onCancel}
                    className="cancel-btn"
                  >
                    Cancel
                  </button>
                  {submitCount > 0 && !isValid ? (
                    <Tooltip
                      title="Please resolve all errors before submitting."
                      placement="top"
                    >
                      <span>
                        <button
                          type="submit"
                          autoFocus
                          className="action-btn"
                          disabled
                          style={{
                            opacity: 0.5,
                            cursor: "not-allowed",
                          }}
                        >
                          Save
                        </button>
                      </span>
                    </Tooltip>
                  ) : (
                    <button
                      type="submit"
                      autoFocus
                      className="action-btn"
                      style={{
                        opacity: 1,
                        cursor: "pointer",
                      }}
                    >
                      Save
                    </button>
                  )}
                </div>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default CompanyDashboardEditObjectivePopup;
